import type { SubscriptionStatusColor } from '../../utils/subscriptionStatusColor';
import type {
  MldDealDates,
  MldDealView,
  MldLastPayment,
  MldLedgerEntryDto,
  MldStatus,
} from '../../api/mld/types';
import {
  mldConvertsOn,
  mldLastPaymentItemDescription,
} from '../../api/mld/readers';
import { formatMessage, mldStrings } from './strings';

/**
 * Pure display helpers shared by the MLD list, the deal detail and the
 * Member › MLD tab. DS returns codes (ADR-007); these turn them into the words
 * in `strings.ts`. No React, no network: every function is a plain mapping
 * over the `api/mld/types.ts` shapes.
 *
 * Rule shared by every label map: an unknown or missing code renders as
 * "Unknown", never as a guessed state (same idea as
 * `printShop/RmQueue/helpers.ts`'s `personalizationDisplay`).
 */

const S = mldStrings;

/**
 * The time zone and "today" used for dates. Both default to the operator's
 * browser; tests inject them so the output is deterministic.
 */
export interface MldFormatOptions {
  /** IANA time zone, e.g. `UTC` or `America/New_York`. */
  timeZone?: string;
  /** The current instant; decides whether the year is shown. */
  now?: Date;
}

// ---------------------------------------------------------------------------
// Label maps
// ---------------------------------------------------------------------------

/**
 * Looks a code up in a label map. `hasOwnProperty` keeps inherited keys such
 * as `toString` from passing for a known code.
 */
const lookup = (map: Readonly<Record<string, string>>, code: unknown): string =>
  typeof code === 'string' && Object.prototype.hasOwnProperty.call(map, code)
    ? map[code]
    : S.unknown;

export const mldStatusLabel = (status: unknown): string =>
  lookup(S.status, status);

export const mldSaleTypeLabel = (saleType: unknown): string =>
  lookup(S.saleType, saleType);

export const mldSubscriptionTypeLabel = (subscriptionType: unknown): string =>
  lookup(S.subscriptionType, subscriptionType);

export const mldFamilyLabel = (family: unknown): string =>
  lookup(S.family, family);

export const mldLedgerKindLabel = (kind: unknown): string =>
  lookup(S.ledgerKind, kind);

/**
 * Who took a payment, changed a status or scheduled a cancellation, in the one
 * MLD vocabulary (ADR-007 decision 1): "Automatic", "Customer",
 * "Support · <email>" ("Support" with no e-mail) or "Authorify (system)".
 * `null` when nobody can be named: `SYSTEM` (an import, or an event of unknown
 * origin, parity ADR-005) and any unrecognised code, which screens show as
 * "—". An explicit switch rather than a map lookup, so the
 * `SUPPORT_WITH_EMAIL` template key can never be mistaken for an actor code.
 */
export const mldWho = (
  actor: unknown,
  operatorEmail?: string | null,
): string | null => {
  switch (actor) {
    case 'AUTOMATIC':
      return S.who.AUTOMATIC;
    case 'CUSTOMER':
      return S.who.CUSTOMER;
    case 'SUPPORT':
      return operatorEmail
        ? formatMessage(S.who.SUPPORT_WITH_EMAIL, { email: operatorEmail })
        : S.who.SUPPORT;
    case 'AUTHORIFY_SYSTEM':
      return S.who.AUTHORIFY_SYSTEM;
    default:
      return null;
  }
};

/**
 * The sale-type part of a payment line, or `null` when the payment has none
 * (a customer's own one-time charge or an import), so the line leaves it out
 * instead of showing a "—" placeholder. Any code goes through
 * `mldSaleTypeLabel`, so an unrecognised one still reads "Unknown".
 *
 * The paid trial invoice is a sale that keeps the "Trial sale" flag (ADR-002
 * decision 1): it reads "Non-Contracted Sale · Trial sale", as the deal detail
 * shows the sale type plus the "Trial sale" badge. A trial payment with no
 * sale type reads "Trial sale" alone.
 */
export const mldPaymentSaleTypeLabel = (
  saleType: unknown,
  trialPayment?: boolean,
): string | null => {
  const hasSaleType = saleType !== null && saleType !== undefined;
  if (trialPayment) {
    return hasSaleType
      ? formatMessage(S.trialPaymentSaleType, {
          saleType: mldSaleTypeLabel(saleType),
          trialSale: S.chip.trialSale,
        })
      : S.chip.trialSale;
  }
  return hasSaleType ? mldSaleTypeLabel(saleType) : null;
};

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

/** Integer cents in the record's currency: `formatCents(4900, 'USD')` → "$49.00". */
export const formatCents = (
  cents: number | null | undefined,
  currency: string | null | undefined,
): string => {
  if (cents === null || cents === undefined || !Number.isFinite(cents)) {
    return S.none;
  }
  const major = cents / 100;
  if (currency) {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
      }).format(major);
    } catch {
      // An unrecognised currency code; fall through to the bare amount.
    }
  }
  return major.toFixed(2);
};

const parseDate = (iso: string | null | undefined): Date | null => {
  if (!iso) {
    return null;
  }
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
};

const dateTimeFormat = (
  options: Intl.DateTimeFormatOptions,
  timeZone: string | undefined,
): Intl.DateTimeFormat => {
  try {
    return new Intl.DateTimeFormat('en-US', { ...options, timeZone });
  } catch {
    // An invalid time zone; fall back to the browser's own.
    return new Intl.DateTimeFormat('en-US', options);
  }
};

/**
 * "Nov 3" in the current year, "Nov 3, 2027" otherwise, both read in the
 * operator's time zone (or the injected one). Missing or invalid → "—".
 */
export const formatMldDate = (
  iso: string | null | undefined,
  options: MldFormatOptions = {},
): string => {
  const date = parseDate(iso);
  if (!date) {
    return S.none;
  }
  const yearFormat = dateTimeFormat({ year: 'numeric' }, options.timeZone);
  const sameYear =
    yearFormat.format(date) === yearFormat.format(options.now ?? new Date());
  return dateTimeFormat(
    sameYear
      ? { month: 'short', day: 'numeric' }
      : { month: 'short', day: 'numeric', year: 'numeric' },
    options.timeZone,
  ).format(date);
};

/**
 * Some ICU builds put a narrow or non-breaking space before "AM"/"PM" and
 * after commas; a plain space keeps the text the same in every browser and in
 * tests.
 */
const plainSpaces = (text: string): string =>
  text.replace(/[\u202f\u00a0]/g, ' ');

/**
 * The time of day in the operator's time zone (or the injected one):
 * "1:30 PM". Missing or invalid → "—".
 */
export const formatMldTime = (
  iso: string | null | undefined,
  options: MldFormatOptions = {},
): string => {
  const date = parseDate(iso);
  if (!date) {
    return S.none;
  }
  return plainSpaces(
    dateTimeFormat(
      { hour: 'numeric', minute: '2-digit' },
      options.timeZone,
    ).format(date),
  );
};

/**
 * A row's date and time, "Oct 8 · 1:30 PM", with the year added outside the
 * current year as `formatMldDate` does ("Oct 8, 2025 · 1:30 PM"). Missing or
 * invalid → "—".
 */
export const formatMldDateTime = (
  iso: string | null | undefined,
  options: MldFormatOptions = {},
): string => {
  if (!parseDate(iso)) {
    return S.none;
  }
  return formatMessage(S.dateTime, {
    date: formatMldDate(iso, options),
    time: formatMldTime(iso, options),
  });
};

/**
 * The full instant for a `<time title>`: "Oct 8, 2026, 1:30:45 PM EDT",
 * always with the year, the seconds and the zone abbreviation. Missing or
 * invalid → "—".
 */
export const formatMldTimestamp = (
  iso: string | null | undefined,
  options: MldFormatOptions = {},
): string => {
  const date = parseDate(iso);
  if (!date) {
    return S.none;
  }
  return plainSpaces(
    dateTimeFormat(
      {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
        timeZoneName: 'short',
      },
      options.timeZone,
    ).format(date),
  );
};

// ---------------------------------------------------------------------------
// Ledger lookups
// ---------------------------------------------------------------------------

const timeOf = (iso: string): number => {
  const time = Date.parse(iso);
  return Number.isNaN(time) ? Number.NEGATIVE_INFINITY : time;
};

/**
 * The most recent entry matching `predicate`. The ledger is oldest first, so
 * on a tie the later entry wins.
 */
const latestEntry = (
  ledger: readonly MldLedgerEntryDto[] | null | undefined,
  predicate: (entry: MldLedgerEntryDto) => boolean,
): MldLedgerEntryDto | null =>
  (ledger ?? []).reduce<MldLedgerEntryDto | null>(
    (latest, entry) =>
      predicate(entry) && (!latest || timeOf(entry.at) >= timeOf(latest.at))
        ? entry
        : latest,
    null,
  );

/** Ledger entries of one kind, oldest first (stable for equal timestamps). */
const entriesOfKind = (
  ledger: readonly MldLedgerEntryDto[] | null | undefined,
  kind: MldLedgerEntryDto['kind'],
): MldLedgerEntryDto[] =>
  (ledger ?? [])
    .map((entry, index) => ({ entry, index }))
    .filter(({ entry }) => entry.kind === kind)
    .sort(
      (a, b) => timeOf(a.entry.at) - timeOf(b.entry.at) || a.index - b.index,
    )
    .map(({ entry }) => entry);

// ---------------------------------------------------------------------------
// Status colour and chip
// ---------------------------------------------------------------------------

/**
 * The flowbite Badge colour of a deal status, in the vocabulary of
 * `utils/subscriptionStatusColor.ts` (which is shared and stays unchanged).
 * The colour is never shown alone: the chip always carries its text.
 */
export const mldStatusColor = (
  status: unknown,
  cancelsOn?: string | null,
): SubscriptionStatusColor => {
  switch (status) {
    case 'TRIAL':
      return 'info';
    case 'ACTIVE':
      return cancelsOn ? 'warning' : 'success';
    case 'PAYMENT_DUE':
      return 'warning';
    case 'ON_HOLD':
      return 'gray';
    case 'CANCELLED':
      return 'failure';
    default:
      return 'gray';
  }
};

/** What the status chip needs; a list row has no ledger, a deal view does. */
export interface MldStatusChipInput {
  status: MldStatus | null;
  cancelsOn: string | null;
  openFailure: MldDealView['openFailure'];
  trialSale: boolean;
  ledger?: readonly MldLedgerEntryDto[] | null;
}

export interface MldStatusChipView {
  /** The status word alone, e.g. "Active". */
  label: string;
  /** What the status needs to be read correctly, e.g. "Cancels Nov 3". */
  context: string | null;
  /** The chip text: the label, plus " · <context>" when there is one. */
  text: string;
  color: SubscriptionStatusColor;
  /** "Trial sale" when the deal began with a trial, as its own marker. */
  trialSaleLabel: string | null;
}

const chipContext = (
  deal: MldStatusChipInput,
  options: MldFormatOptions,
): string | null => {
  switch (deal.status) {
    case 'TRIAL':
    case 'ACTIVE': {
      // ADR-002: a scheduled cancellation keeps the deal Active (or in
      // Trial, for a trial set to cancel at its end) with this marker.
      if (deal.cancelsOn) {
        return formatMessage(S.chip.cancels, {
          date: formatMldDate(deal.cancelsOn, options),
        });
      }
      if (deal.status !== 'ACTIVE') {
        return null;
      }
      const lastChange = latestEntry(
        deal.ledger,
        (entry) => entry.kind === 'STATUS_CHANGE',
      );
      return lastChange?.fromStatus === 'PAYMENT_DUE' &&
        lastChange.toStatus === 'ACTIVE'
        ? formatMessage(S.chip.backFromPaymentDue, {
            date: formatMldDate(lastChange.at, options),
          })
        : null;
    }
    case 'PAYMENT_DUE':
      if (!deal.openFailure) {
        return null;
      }
      return (
        deal.openFailure.failureReason || deal.openFailure.invoiceId || null
      );
    default:
      return null;
  }
};

/**
 * The status chip: "Active · Cancels Nov 3", "Payment due · card declined",
 * "Active · Back from payment due Oct 4", or the bare status word.
 */
export const mldStatusChip = (
  deal: MldStatusChipInput,
  options: MldFormatOptions = {},
): MldStatusChipView => {
  const label = mldStatusLabel(deal.status);
  const context = chipContext(deal, options);
  return {
    label,
    context,
    text: context
      ? formatMessage(S.chip.withContext, { status: label, context })
      : label,
    color: mldStatusColor(deal.status, deal.cancelsOn),
    trialSaleLabel: deal.trialSale ? S.chip.trialSale : null,
  };
};

/** What `mldCancellationScheduledLine` reads from a deal. */
export interface MldCancellationLineInput {
  status: MldStatus | null;
  ledger?: readonly MldLedgerEntryDto[] | null;
}

/**
 * "Cancellation scheduled on <date> by <who>", from the latest
 * `CANCELLATION_SCHEDULED` entry, or "Cancellation scheduled on <date>" when
 * nobody can be named (`mldWho`). `null` when none exists, when a later
 * `CANCELLATION_REMOVED` withdrew it, or once the deal is cancelled: the
 * schedule has happened, so the line would describe the past as the future
 * (ADR-007 decision 8).
 */
export const mldCancellationScheduledLine = (
  deal: MldCancellationLineInput,
  options: MldFormatOptions = {},
): string | null => {
  if (deal.status === 'CANCELLED') {
    return null;
  }
  const latest = latestEntry(
    deal.ledger,
    (entry) =>
      entry.kind === 'CANCELLATION_SCHEDULED' ||
      entry.kind === 'CANCELLATION_REMOVED',
  );
  if (!latest || latest.kind !== 'CANCELLATION_SCHEDULED') {
    return null;
  }
  const date = formatMldDate(latest.at, options);
  const who = mldWho(latest.actor, latest.operatorEmail);
  return who
    ? formatMessage(S.chip.cancellationScheduled, { date, who })
    : formatMessage(S.chip.cancellationScheduledNoWho, { date });
};

// ---------------------------------------------------------------------------
// Payments
// ---------------------------------------------------------------------------

/**
 * A payment's amount (ADR-007 decision 11). A sale-typed `PAYMENT` whose sale
 * amount (`grossAmountCents`, the invoice total with tax, before credits)
 * differs from what was paid reads "$216.00 paid · $538.91 sale"; any other
 * entry reads the amount alone ("$216.00"), as before.
 */
export const mldPaymentAmount = (
  entry: Pick<
    MldLedgerEntryDto,
    'kind' | 'amountCents' | 'currency' | 'saleType' | 'grossAmountCents'
  >,
): string => {
  const paid = formatCents(entry.amountCents, entry.currency);
  const gross = entry.grossAmountCents;
  const showsSale =
    entry.kind === 'PAYMENT' &&
    Boolean(entry.saleType) &&
    typeof gross === 'number' &&
    Number.isFinite(gross) &&
    typeof entry.amountCents === 'number' &&
    Number.isFinite(entry.amountCents) &&
    gross !== entry.amountCents;
  return showsSale
    ? formatMessage(S.detail.payments.paidAndSale, {
        paid,
        gross: formatCents(gross, entry.currency),
      })
    : paid;
};

/** The two lines of the list's "Last payment" cell. */
export interface MldLastPaymentCell {
  /** Line 1: the paid date, "Oct 4" (or "Oct 4, 2025" in another year). */
  date: string;
  /** Line 2: "amount · sale type · who", with missing parts left out. */
  detail: string;
}

/**
 * The list's date-first "Last payment" cell (ADR-001): the paid date on its
 * own line, then "$49.00 · Billed-through · Automatic" (who from `mldWho`). A
 * missing sale type or who is left out ("$99.00"), never shown as "—" or
 * "Chargebee".
 * `null` when the deal has no payment, so the cell shows "—".
 */
export const mldLastPaymentCell = (
  payment: MldLastPayment | null | undefined,
  options: MldFormatOptions = {},
): MldLastPaymentCell | null => {
  if (!payment) {
    return null;
  }
  return {
    date: formatMldDate(payment.paidAt, options),
    detail: [
      formatCents(payment.amountCents, payment.currency),
      mldPaymentSaleTypeLabel(payment.saleType, payment.trialPayment),
      mldWho(payment.actor, payment.operatorEmail),
    ]
      .filter((part): part is string => Boolean(part))
      .join(S.lastPaymentSeparator),
  };
};

/**
 * The deal card's "Last payment" fact (ADR-007 decision 10): "$322.92 · Oct 8
 * · Automatic", from the same formatters and who as `mldLastPaymentCell`. A
 * who that cannot be named (`SYSTEM`, an unknown code) is left out, never
 * shown as "—". "No payment yet" when the deal has no payment.
 */
export const mldLastPaymentSummary = (
  payment: MldLastPayment | null | undefined,
  options: MldFormatOptions = {},
): string => {
  if (!payment) {
    return S.lastPayment.none;
  }
  return [
    formatCents(payment.amountCents, payment.currency),
    formatMldDate(payment.paidAt, options),
    mldWho(payment.actor, payment.operatorEmail),
  ]
    .filter((part): part is string => Boolean(part))
    .join(S.lastPaymentSeparator);
};

// ---------------------------------------------------------------------------
// Dates that matter now
// ---------------------------------------------------------------------------

export type MldDateKey = keyof typeof S.dates;

export interface MldDateField {
  key: MldDateKey;
  label: string;
  value: string;
}

/**
 * `convertsOn` is optional here so a payload from a DS deploy that does not
 * send it yet reads as `null` (no "Converts on"), through `mldConvertsOn`.
 */
export type MldDatesInput = Pick<
  MldDealView,
  | 'status'
  | 'cancelsOn'
  | 'nextPaymentAt'
  | 'openFailure'
  | 'plan'
  | 'lastPayment'
> & {
  dates:
    | (Omit<MldDealDates, 'convertsOn'> &
        Partial<Pick<MldDealDates, 'convertsOn'>>)
    | null;
};

const dateField = (
  key: MldDateKey,
  iso: string | null | undefined,
  options: MldFormatOptions,
): MldDateField => ({
  key,
  label: S.dates[key],
  value: formatMldDate(iso, options),
});

const moneyField = (
  key: MldDateKey,
  cents: number | null | undefined,
  currency: string | null | undefined,
): MldDateField => ({
  key,
  label: S.dates[key],
  value: formatCents(cents, currency),
});

/**
 * "Next billing date" or, when a cancellation is scheduled, "Cancels on" instead.
 * `null` for a cancelled deal, which has no next payment.
 */
export const mldNextPaymentOrCancels = (
  deal: Pick<MldDealView, 'status' | 'cancelsOn' | 'nextPaymentAt'>,
  options: MldFormatOptions = {},
): MldDateField | null => {
  if (deal.status === 'CANCELLED') {
    return null;
  }
  return deal.cancelsOn
    ? dateField('cancelsOn', deal.cancelsOn, options)
    : dateField('nextPayment', deal.nextPaymentAt, options);
};

/**
 * Only the dates that matter in the current status (PRD UI checklist: hide
 * what does not apply). A missing value still shows its label, with "—".
 * A paid trial with a conversion date reads "Converts on" in place of "Trial
 * ends" (ADR-007 decision 9).
 */
export const mldVisibleDates = (
  deal: MldDatesInput,
  options: MldFormatOptions = {},
): MldDateField[] => {
  const dates = deal.dates;
  const convertsOn = mldConvertsOn(dates);
  switch (deal.status) {
    case 'TRIAL':
      return [
        convertsOn
          ? dateField('convertsOn', convertsOn, options)
          : dateField('trialEnd', dates?.trialEnd, options),
        deal.cancelsOn
          ? dateField('cancelsOn', deal.cancelsOn, options)
          : moneyField(
              'firstFullCharge',
              deal.plan.amountCents,
              deal.plan.currency,
            ),
      ];
    case 'ACTIVE':
      return [
        dateField('activeSince', dates?.activeSince, options),
        deal.cancelsOn
          ? dateField('cancelsOn', deal.cancelsOn, options)
          : dateField('nextPayment', deal.nextPaymentAt, options),
      ];
    case 'PAYMENT_DUE':
      return [
        dateField('failedOn', deal.openFailure?.failedAt, options),
        moneyField(
          'amountDue',
          deal.openFailure?.amountDueCents,
          deal.plan.currency ?? deal.lastPayment?.currency,
        ),
      ];
    case 'ON_HOLD':
      return [
        dateField('onHoldSince', dates?.onHoldSince, options),
        dateField('resumesOn', dates?.resumesOn, options),
      ];
    case 'CANCELLED':
      return [dateField('cancelledOn', dates?.cancelledAt, options)];
    default:
      return [];
  }
};

// ---------------------------------------------------------------------------
// Lifecycle stepper
// ---------------------------------------------------------------------------

export type MldStageKey = keyof typeof S.stepper.stages;

/** Trial → Active → On hold / Payment due → Cancelled. */
export const MLD_STAGES: readonly MldStageKey[] = [
  'trial',
  'active',
  'interrupted',
  'cancelled',
];

const STAGE_OF_STATUS: Readonly<Record<MldStatus, MldStageKey>> = {
  TRIAL: 'trial',
  ACTIVE: 'active',
  PAYMENT_DUE: 'interrupted',
  ON_HOLD: 'interrupted',
  CANCELLED: 'cancelled',
};

const stageOf = (status: unknown): MldStageKey | null =>
  typeof status === 'string' &&
  Object.prototype.hasOwnProperty.call(STAGE_OF_STATUS, status)
    ? STAGE_OF_STATUS[status as MldStatus]
    : null;

/** The current stage index, or `null` (no stage marked) for an unknown status. */
export const mldStageIndex = (status: unknown): number | null => {
  const stage = stageOf(status);
  return stage === null ? null : MLD_STAGES.indexOf(stage);
};

/** Where the deal stands against a stage, read from its history (ADR-007 decision 6). */
export type MldStageState = 'current' | 'visited' | 'notReached';

export interface MldStepperStage {
  key: MldStageKey;
  label: string;
  /**
   * `current` for the stage of the deal's status; `visited` when the history
   * shows the deal in this stage (or, for Trial, when it began with a trial
   * sale); `notReached` otherwise. Never inferred from the stage's position.
   */
  state: MldStageState;
  /** The visually hidden word for `state`: "Current stage", "Visited"… */
  stateLabel: string;
  /** `state === 'current'`, for `aria-current`. */
  current: boolean;
  /** Each period the deal spent in this stage, oldest first. */
  ranges: string[];
  /**
   * What a stage the deal never reached reads instead of a period: "No
   * trial", "Not scheduled" or "Scheduled Nov 8". `null` for any other stage.
   */
  placeholder: string | null;
}

export interface MldStepper {
  currentIndex: number | null;
  stages: MldStepperStage[];
  /**
   * The period still open, captioned "<Status> since <start>" in the stepper:
   * the status the last `STATUS_CHANGE` entered and its formatted start, or,
   * when no change entered the current status, the current status from
   * `statusSince`. `null` when neither is known (no caption is shown then).
   */
  openPeriod: { status: MldStatus; start: string } | null;
}

/** What the stepper reads from a deal; an `MldDealView` fits as it is. */
export interface MldStepperInput {
  status: MldStatus | null;
  ledger?: readonly MldLedgerEntryDto[] | null;
  cancelsOn?: string | null;
  /** ISO-8601: when the current status began. */
  statusSince?: string | null;
  trialSale?: boolean;
  imported?: boolean;
}

/** The moment the deal entered `toStatus`. */
interface StatusBoundary {
  at: string;
  toStatus: MldStatus | null | undefined;
}

/**
 * The `STATUS_CHANGE` entries, oldest first. When the last one did not enter
 * the current status (an imported deal whose history starts at the import
 * has none, ADR-006), `statusSince` is added as the start of the open
 * period, unless it is missing, unreadable or earlier than that last change.
 */
const statusBoundaries = (deal: MldStepperInput): StatusBoundary[] => {
  const changes: StatusBoundary[] = entriesOfKind(
    deal.ledger,
    'STATUS_CHANGE',
  ).map(({ at, toStatus }) => ({ at, toStatus }));
  const last = changes[changes.length - 1];
  const since = deal.statusSince;
  if (
    !since ||
    !parseDate(since) ||
    !stageOf(deal.status) ||
    (last && (last.toStatus === deal.status || timeOf(since) < timeOf(last.at)))
  ) {
    return changes;
  }
  return [...changes, { at: since, toStatus: deal.status }];
};

/**
 * The periods between consecutive boundaries, each filed under the stage of
 * the status it entered: "Payment due Sep 16 – Sep 18", or "Active since Sep
 * 18" for the period still open.
 */
const stageRanges = (
  boundaries: readonly StatusBoundary[],
  options: MldFormatOptions,
): Map<MldStageKey, string[]> => {
  const ranges = new Map<MldStageKey, string[]>();
  boundaries.forEach((boundary, index) => {
    const stage = stageOf(boundary.toStatus);
    if (!stage) {
      return;
    }
    const status = mldStatusLabel(boundary.toStatus);
    const start = formatMldDate(boundary.at, options);
    const next = boundaries[index + 1];
    let text: string;
    if (!next) {
      text = formatMessage(S.stepper.since, { status, start });
    } else {
      const end = formatMldDate(next.at, options);
      text =
        end === start
          ? formatMessage(S.stepper.singleDay, { status, start })
          : formatMessage(S.stepper.range, { status, start, end });
    }
    ranges.set(stage, [...(ranges.get(stage) ?? []), text]);
  });
  return ranges;
};

const openPeriod = (
  boundaries: readonly StatusBoundary[],
  options: MldFormatOptions,
): MldStepper['openPeriod'] => {
  const last = boundaries[boundaries.length - 1];
  if (!last || !stageOf(last.toStatus)) {
    return null;
  }
  return {
    status: last.toStatus as MldStatus,
    start: formatMldDate(last.at, options),
  };
};

/**
 * The stages the history shows the deal in: every stage with a period, every
 * stage a status change left (an imported deal's first recorded change leaves
 * the status it was imported in), and Trial for a deal that began with a
 * trial sale.
 */
const visitedStages = (
  deal: MldStepperInput,
  ranges: ReadonlyMap<MldStageKey, string[]>,
): Set<MldStageKey> => {
  const visited = new Set<MldStageKey>(ranges.keys());
  entriesOfKind(deal.ledger, 'STATUS_CHANGE').forEach((change) => {
    const stage = stageOf(change.fromStatus);
    if (stage) {
      visited.add(stage);
    }
  });
  if (deal.trialSale) {
    visited.add('trial');
  }
  return visited;
};

/**
 * The placeholder of a stage the deal never reached: Trial reads "No trial"
 * (left out on an imported deal, whose history before the import is
 * unknown); Cancelled reads "Scheduled <date>" while a cancellation is
 * scheduled, else "Not scheduled".
 */
const notReachedPlaceholder = (
  key: MldStageKey,
  deal: MldStepperInput,
  options: MldFormatOptions,
): string | null => {
  const words = S.stepper.placeholders;
  switch (key) {
    case 'trial':
      return deal.imported ? null : words.noTrial;
    case 'cancelled':
      return parseDate(deal.cancelsOn)
        ? formatMessage(words.scheduled, {
            date: formatMldDate(deal.cancelsOn, options),
          })
        : words.notScheduled;
    default:
      return null;
  }
};

/**
 * Trial → Active → On hold / Payment due → Cancelled, drawn from the deal's
 * history (ADR-007 decision 6): the current stage, the stages it was in, and
 * the ones it never reached with their placeholders.
 */
export const mldLifecycleStepper = (
  deal: MldStepperInput,
  options: MldFormatOptions = {},
): MldStepper => {
  const currentIndex = mldStageIndex(deal.status);
  const boundaries = statusBoundaries(deal);
  const ranges = stageRanges(boundaries, options);
  const visited = visitedStages(deal, ranges);
  return {
    currentIndex,
    stages: MLD_STAGES.map((key, index) => {
      const state: MldStageState =
        index === currentIndex
          ? 'current'
          : visited.has(key)
          ? 'visited'
          : 'notReached';
      return {
        key,
        label: S.stepper.stages[key],
        state,
        stateLabel: S.stepper.state[state],
        current: state === 'current',
        ranges: ranges.get(key) ?? [],
        placeholder:
          state === 'notReached'
            ? notReachedPlaceholder(key, deal, options)
            : null,
      };
    }),
    openPeriod: openPeriod(boundaries, options),
  };
};

/**
 * The "<Status> since" date fields, keyed by the status whose open period the
 * stepper captions with the same words ("Active since <date>", "On hold since
 * <date>").
 */
const SINCE_FIELD_STATUS: Partial<Record<MldDateKey, MldStatus>> = {
  activeSince: 'ACTIVE',
  onHoldSince: 'ON_HOLD',
};

/**
 * The dates of a Member › MLD card (UI checklist: no repeated values). Same
 * as `mldVisibleDates`, without what the card already shows elsewhere:
 * - "Cancels on": the header chip reads "Cancels <date>";
 * - "Active since" / "On hold since": dropped whenever the stepper caption
 *   reads "<Status> since <date>" for that same status. Every period is
 *   already in the stepper, so a card never shows the same "since" twice.
 *   "Resumes on" is not in the stepper and stays.
 */
export const mldMemberCardDates = (
  deal: MldDatesInput,
  stepper: Pick<MldStepper, 'openPeriod'>,
  options: MldFormatOptions = {},
): MldDateField[] =>
  mldVisibleDates(deal, options).filter((field) => {
    if (field.key === 'cancelsOn') {
      return false;
    }
    const sinceStatus = SINCE_FIELD_STATUS[field.key];
    if (sinceStatus) {
      return stepper.openPeriod?.status !== sinceStatus;
    }
    return true;
  });

// ---------------------------------------------------------------------------
// Deal detail: Chargebee links, ledger split, history wording, warnings
// ---------------------------------------------------------------------------

/**
 * `<chargebeeAppUrl>/d/<segment>/<id>`, the format DS already builds for the
 * Subscriptions tab, with the id URI-encoded. `null` when either part is
 * missing, so the page hides the link instead of rendering a broken one.
 */
const chargebeeAppLink = (
  chargebeeAppUrl: string | null | undefined,
  segment: 'subscriptions' | 'customers' | 'invoices',
  id: string | null | undefined,
): string | null => {
  const base = (chargebeeAppUrl ?? '').trim().replace(/\/+$/, '');
  if (!base || !id) {
    return null;
  }
  return `${base}/d/${segment}/${encodeURIComponent(id)}`;
};

export const mldChargebeeSubscriptionUrl = (
  chargebeeAppUrl: string | null | undefined,
  subscriptionId: string | null | undefined,
): string | null =>
  chargebeeAppLink(chargebeeAppUrl, 'subscriptions', subscriptionId);

export const mldChargebeeCustomerUrl = (
  chargebeeAppUrl: string | null | undefined,
  chargebeeCustomerId: string | null | undefined,
): string | null =>
  chargebeeAppLink(chargebeeAppUrl, 'customers', chargebeeCustomerId);

export const mldChargebeeInvoiceUrl = (
  chargebeeAppUrl: string | null | undefined,
  invoiceId: string | null | undefined,
): string | null => chargebeeAppLink(chargebeeAppUrl, 'invoices', invoiceId);

const PAYMENT_KINDS: ReadonlySet<string> = new Set([
  'PAYMENT',
  'PAYMENT_FAILED',
]);

/**
 * A ledger entry as the screens show it: the stored entry, or the display-only
 * `PAYMENT` row `mldDisplayLedger` builds from an imported deal's last payment
 * (ADR-005), flagged `importedPayment`.
 */
export type MldDisplayLedgerEntry = MldLedgerEntryDto & {
  importedPayment?: true;
};

/**
 * Generic over the entry type, so a flag such as `importedPayment` survives
 * the split.
 */
export interface MldLedgerSplit<
  E extends MldLedgerEntryDto = MldLedgerEntryDto,
> {
  /** `PAYMENT` and `PAYMENT_FAILED`, newest first. */
  payments: E[];
  /** Every other kind, newest first. */
  history: E[];
}

/**
 * The ledger in true chronological order, oldest first, the order the derived
 * values read it in (screens list it newest first, see `mldNewestFirst`). DS
 * already sends it that way (ADR-005: sorted by
 * `compareMldEvents`, which also breaks timestamp ties); the sort is stable,
 * so entries with the same timestamp keep the DS order, and it only guards
 * against a payload that is not sorted.
 */
export const mldChronologicalLedger = <E extends MldLedgerEntryDto>(
  ledger: readonly E[] | null | undefined,
): E[] =>
  (Array.isArray(ledger) ? ledger : [])
    .map((entry, index) => ({ entry, index }))
    .sort(
      (a, b) => timeOf(a.entry.at) - timeOf(b.entry.at) || a.index - b.index,
    )
    .map(({ entry }) => entry);

/**
 * Items newest first, the order every MLD ledger and history is shown in
 * (2026-10-08 user decision: the most recent entry on top). It is the exact
 * reverse of the chronological order: items with the same timestamp show the
 * later one in the input order first, and an unreadable `at` goes last. The
 * derived values (latest entry, stepper periods) keep reading the ledger
 * oldest first; only what is listed on screen is reversed.
 */
export const mldNewestFirst = <T extends { at: string }>(
  items: readonly T[] | null | undefined,
): T[] =>
  (Array.isArray(items) ? items : [])
    .map((item, index) => ({ item, index }))
    .sort((a, b) => timeOf(b.item.at) - timeOf(a.item.at) || b.index - a.index)
    .map(({ item }) => item);

/**
 * Splits the ledger into the payments table and the status history (TechSpec
 * route 3), both newest first.
 */
export const mldSplitLedger = <E extends MldLedgerEntryDto>(
  ledger: readonly E[] | null | undefined,
): MldLedgerSplit<E> =>
  mldNewestFirst(ledger).reduce<MldLedgerSplit<E>>(
    (split, entry) => {
      (PAYMENT_KINDS.has(entry.kind) ? split.payments : split.history).push(
        entry,
      );
      return split;
    },
    { payments: [], history: [] },
  );

/** A ledger with its import note taken out of the dated rows. */
export interface MldImportSplit<E extends MldLedgerEntryDto> {
  /** Every entry but `IMPORTED`, in the order given. */
  entries: E[];
  /** The earliest `IMPORTED` entry, or `null` when the deal was not imported. */
  imported: E | null;
}

/**
 * Takes the `IMPORTED` entries out of a ledger (ADR-007 decision 7): the
 * import is where the history starts, not an event, so it is shown as one
 * footnote (`mldImportFootnote`) instead of a dated row, a filter match or a
 * count. The earliest one is kept (on a tie, the first one given).
 */
export const mldSplitImported = <E extends MldLedgerEntryDto>(
  ledger: readonly E[] | null | undefined,
): MldImportSplit<E> =>
  (Array.isArray(ledger) ? ledger : []).reduce<MldImportSplit<E>>(
    (split, entry) => {
      if (entry.kind !== 'IMPORTED') {
        split.entries.push(entry);
      } else if (
        !split.imported ||
        timeOf(entry.at) < timeOf(split.imported.at)
      ) {
        split.imported = entry;
      }
      return split;
    },
    { entries: [], imported: null },
  );

/**
 * "Imported from Chargebee on <date>. Earlier history is in Chargebee.", the
 * muted line under a ledger whose history starts at an import, or `null`
 * without one.
 */
export const mldImportFootnote = (
  imported: Pick<MldLedgerEntryDto, 'at'> | null | undefined,
  options: MldFormatOptions = {},
): string | null =>
  imported
    ? formatMessage(S.importFootnote, {
        date: formatMldDate(imported.at, options),
      })
    : null;

/** What `mldDisplayLedger` reads from a deal. */
export type MldDisplayLedgerInput = Pick<
  MldDealView,
  'imported' | 'lastPayment' | 'ledger'
>;

/**
 * The deal's ledger with what every screen shows, oldest first (ADR-005);
 * screens list it through `mldNewestFirst`. An imported deal's ledger holds
 * only the `IMPORTED` note (parent ADR-006
 * invents no payment), while the list reads `lastPayment` from the import
 * snapshot. So when the deal is imported, has a `lastPayment`, and no
 * `PAYMENT` entry carries that invoice id, one display-only `PAYMENT` row is
 * built from `lastPayment`, flagged `importedPayment`, in its chronological
 * place (before an entry with the same timestamp, since the payment predates
 * the import). Otherwise the chronological ledger is returned as it is.
 */
export const mldDisplayLedger = (
  deal: MldDisplayLedgerInput,
): MldDisplayLedgerEntry[] => {
  const ledger: readonly MldDisplayLedgerEntry[] = Array.isArray(deal.ledger)
    ? deal.ledger
    : [];
  const payment = deal.lastPayment;
  const alreadyInLedger =
    payment &&
    ledger.some(
      (entry) =>
        entry.kind === 'PAYMENT' && entry.invoiceId === payment.invoiceId,
    );
  if (deal.imported !== true || !payment || alreadyInLedger) {
    return mldChronologicalLedger(ledger);
  }
  const importedPayment: MldDisplayLedgerEntry = {
    eventId: `imported-payment:${payment.invoiceId}`,
    at: payment.paidAt,
    kind: 'PAYMENT',
    actor: payment.actor,
    operatorEmail: payment.operatorEmail,
    invoiceId: payment.invoiceId,
    amountCents: payment.amountCents,
    currency: payment.currency,
    saleType: payment.saleType,
    trialPayment: payment.trialPayment,
    // What the snapshot's invoice bought, so the row's Item is not blank.
    itemDescription: mldLastPaymentItemDescription(payment) ?? undefined,
    importedPayment: true,
  };
  return mldChronologicalLedger([importedPayment, ...ledger]);
};

/**
 * The invoice ids of the payments in the order given (newest first on the
 * deal detail), without repeats (a failed invoice and the payment that
 * settles it share one id; the first one seen is kept).
 */
export const mldPaymentInvoiceIds = (
  payments: readonly MldLedgerEntryDto[],
): string[] =>
  Array.from(
    new Set(
      payments
        .map((entry) => entry.invoiceId)
        .filter((id): id is string => Boolean(id)),
    ),
  );

/** A plan's display name keyed by its Chargebee `itemPriceId`. */
export type MldPlanNameLookup = ReadonlyMap<string, string>;

const EMPTY_PLAN_NAMES: MldPlanNameLookup = new Map();

/** What `mldPlanNameLookup` reads from each deal. */
export type MldPlanNameLookupInput = Pick<MldDealView, 'plan' | 'ledger'>;

const nonBlank = (value: unknown): value is string =>
  typeof value === 'string' && value.trim() !== '';

/**
 * Every plan name the member's deals carry (ADR-007 decision 2), so a plan
 * change reads "Authorify Premium", not `AFY-Premium-NC-USD-Monthly`. Filled
 * from each `PAYMENT` / `PAYMENT_FAILED` entry that has both `itemPriceId`
 * and `planName` (a later entry wins over an earlier one), then from each
 * deal's own `plan`, which wins on a conflict.
 */
export const mldPlanNameLookup = (
  deals: readonly MldPlanNameLookupInput[] | null | undefined,
): MldPlanNameLookup => {
  const names = new Map<string, string>();
  const list = Array.isArray(deals) ? deals : [];
  for (const deal of list) {
    for (const entry of mldChronologicalLedger(deal?.ledger)) {
      if (
        PAYMENT_KINDS.has(entry.kind) &&
        nonBlank(entry.itemPriceId) &&
        nonBlank(entry.planName)
      ) {
        names.set(entry.itemPriceId, entry.planName);
      }
    }
  }
  for (const deal of list) {
    const plan = deal?.plan;
    if (plan && nonBlank(plan.itemPriceId) && nonBlank(plan.name)) {
      names.set(plan.itemPriceId, plan.name);
    }
  }
  return names;
};

/**
 * A plan's name from the lookup, else its raw id (an old record, or a plan
 * that was never paid). A missing id reads "Unknown".
 */
export const mldPlanLabel = (
  itemPriceId: string | null | undefined,
  lookup: MldPlanNameLookup = EMPTY_PLAN_NAMES,
): string => {
  if (!nonBlank(itemPriceId)) {
    return S.unknown;
  }
  return lookup.get(itemPriceId) ?? itemPriceId;
};

/**
 * What a payment was for (ADR-007 decision 3): the plan it paid for, named as
 * the lookup names that plan ("Authorify Platinum"); else what a one-time
 * charge bought ("52 printed book credits (upsell offer)"); else `null`.
 */
export const mldPaymentItem = (
  entry: Pick<MldLedgerEntryDto, 'itemPriceId' | 'planName' | 'itemDescription'>,
  lookup: MldPlanNameLookup = EMPTY_PLAN_NAMES,
): string | null => {
  if (nonBlank(entry.planName)) {
    return (
      (nonBlank(entry.itemPriceId) ? lookup.get(entry.itemPriceId) : undefined) ??
      entry.planName
    );
  }
  return nonBlank(entry.itemDescription) ? entry.itemDescription : null;
};

/**
 * What a non-payment ledger entry changed, in plain words. Shared by the deal
 * detail's status history and the Member › MLD ledger, so both read the same.
 * A status change from a missing or unknown status reads "Status set to
 * Active", never "Unknown → Active". A plan change names its plans through
 * `lookup` ("Plan changed from Authorify Premium to Authorify Platinum"), or
 * by raw id without one. A scheduled cancellation that carries its target
 * date reads "Set to cancel on Nov 8".
 */
export const mldHistoryWhat = (
  entry: MldLedgerEntryDto,
  lookup: MldPlanNameLookup = EMPTY_PLAN_NAMES,
  options: MldFormatOptions = {},
): string => {
  const words = S.detail.history;
  switch (entry.kind) {
    case 'STATUS_CHANGE':
      return stageOf(entry.fromStatus)
        ? formatMessage(words.statusChange, {
            from: mldStatusLabel(entry.fromStatus),
            to: mldStatusLabel(entry.toStatus),
          })
        : formatMessage(words.statusSet, {
            to: mldStatusLabel(entry.toStatus),
          });
    case 'PLAN_CHANGE':
      return entry.fromPlan
        ? formatMessage(words.planChange, {
            from: mldPlanLabel(entry.fromPlan, lookup),
            to: mldPlanLabel(entry.toPlan, lookup),
          })
        : formatMessage(words.planSet, {
            to: mldPlanLabel(entry.toPlan, lookup),
          });
    case 'CANCELLATION_SCHEDULED':
      return parseDate(entry.cancelsOn)
        ? formatMessage(words.setToCancel, {
            date: formatMldDate(entry.cancelsOn, options),
          })
        : mldLedgerKindLabel(entry.kind);
    default:
      return mldLedgerKindLabel(entry.kind);
  }
};

/**
 * The plain-words notes for a deal's warnings, one per distinct code, in the
 * order first seen. An unrecognised code reads "Unknown".
 */
export const mldWarningNotes = (
  warnings: readonly { code: string }[] | null | undefined,
): string[] =>
  Array.from(new Set((warnings ?? []).map((warning) => warning.code))).map(
    (code) => lookup(S.detail.warnings.codes, code),
  );
